// Debug logging is off in production; set localStorage.ssFiltersDebug = '1'
// in the console to re-enable the verbose tracing this file shipped with.
function ssFFDebugLog() {
  try {
    if (window.localStorage && localStorage.getItem('ssFiltersDebug')) {
      console.log.apply(console, arguments);
    }
  } catch (e) { /* storage unavailable */ }
}
/**
 * ============================================================================
 * SEARCHSPRING FEATURED FILTERS
 * ============================================================================
 * 
 * WHAT THIS DOES:
 * - Reads featured filter names from collection metafield (custom.filters_featured_options)
 * - Waits for SearchSpring to render filters in sidebar
 * - Finds matching filters and clones them to top bar as custom dropdowns
 * - Creates checkbox-style dropdowns that match SearchSpring's native UI
 * - Syncs selections bidirectionally: top bar ↔ sidebar
 * - Button text always shows filter category name (e.g., "COLOR"), never changes
 * 
 * HOW IT WORKS:
 * 1. DOMContentLoaded → init()
 * 2. init() → Check if SearchSpring sidebar exists
 * 3. Get featured filter names from metafield (data attribute)
 * 4. Wait for SearchSpring to render filters (MutationObserver + polling)
 * 5. Find matching filters in sidebar by name
 * 6. Clone filters to top bar as custom dropdowns with checkbox-style options
 * 7. Setup click handlers that navigate to SearchSpring URLs
 * 8. Watch sidebar for changes and sync top bar checkboxes/selected states
 * 
 * KEY FEATURES:
 * - Always re-finds filters dynamically (no stale references)
 * - Always gets current hrefs from sidebar (not stored values)
 * - Matches options by label text (not href/value which can change)
 * - Directly checks DOM for selected state (not extraction function)
 * ============================================================================
 */

(function() {
  'use strict';

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================
  const CONFIG = {
    sidebarSelector: '#searchspring-sidebar',
    topBarSelector: '.searchspring-unified-filter-bar__featured-filters'
  };

  // ==========================================================================
  // MAIN MANAGER OBJECT
  // ==========================================================================
  function ssFFCurrencySymbol() {
    const el = document.querySelector(CONFIG.topBarSelector);
    return (el && el.getAttribute('data-currency-symbol')) || '\u00a3';
  }

  const Manager = {
    featuredFilterNames: [],
    initialized: false,
    syncTimeout: null,

    // ------------------------------------------------------------------------
    // STEP 1: Initialize - called when DOM is ready
    // ------------------------------------------------------------------------
    init: function() {
      // Prevent duplicate initialization
      if (this.initialized) {
        ssFFDebugLog('[SS Filters] Already initialized, skipping');
        return;
      }
      
      ssFFDebugLog('[SS Filters] Starting...');
      
      // Check if SearchSpring sidebar exists
      if (!document.querySelector(CONFIG.sidebarSelector)) {
        ssFFDebugLog('[SS Filters] No SearchSpring sidebar found');
        return;
      }

      // Get featured filter names from metafield
      this.featuredFilterNames = this.getFeaturedFilterNames();
      if (this.featuredFilterNames.length === 0) {
        ssFFDebugLog('[SS Filters] No featured filters configured');
        return;
      }

      ssFFDebugLog('[SS Filters] Featured filters:', this.featuredFilterNames);

      // Hold the row's space while SearchSpring's facets are still in flight
      this.renderPlaceholder();

      // Wait for SearchSpring to render filters, then setup
      this.waitForFilters();
    },

    // ------------------------------------------------------------------------
    // STEP 2: Get featured filter names from metafield (set by Liquid)
    // ------------------------------------------------------------------------
    getFeaturedFilterNames: function() {
      const container = document.querySelector(CONFIG.topBarSelector);
      if (!container) return [];
      
      const data = container.getAttribute('data-featured-filters');
      if (!data || data === '[]') return [];
      
      try {
        return JSON.parse(data);
      } catch (e) {
        ssFFDebugLog('[SS Filters] Error parsing metafield:', e);
        return [];
      }
    },

    // ------------------------------------------------------------------------
    // Has SearchSpring actually rendered facet data into the sidebar yet?
    // The size of the sidebar's innerHTML is NOT that signal: SearchSpring
    // paints its own wrapper (and, while the search response is in flight,
    // placeholder markup) into #searchspring-sidebar before a single
    // `.ss__facet` exists. A length test therefore reports "ready" too
    // early, the first setupFilters() finds no facets to clone, and the
    // filter row stays empty until the shopper forces another render.
    // Facet presence is the condition every later step actually depends on.
    // ------------------------------------------------------------------------
    facetsReady: function(sidebar) {
      if (!sidebar) return false;
      return sidebar.querySelectorAll('.ss__facet').length > 0;
    },

    // ------------------------------------------------------------------------
    // STEP 3: Wait for SearchSpring to render filters in sidebar
    // ------------------------------------------------------------------------
    waitForFilters: function() {
      const sidebar = document.querySelector(CONFIG.sidebarSelector);
      if (!sidebar) return;
      if (this._waitingForFilters) return; // late-retry guard: one waiter at a time
      this._waitingForFilters = true;

      ssFFDebugLog('[SS Filters] Waiting for filters to render...');

      // Check if the facets have already landed - render immediately, with no
      // interaction required from the shopper.
      if (this.facetsReady(sidebar)) {
        ssFFDebugLog('[SS Filters] Filters already rendered');
        this.setupFilters();
        // Sync will be called in setupFilters, but also sync after a delay to catch any late updates
        setTimeout(() => {
          this.syncTopBarWithSidebar();
        }, 1500);
        return;
      }

      // Watch for when SearchSpring injects content
      // Use passive observation - don't interfere with SearchSpring's rendering
      let hasDisconnected = false;
      let poll = null;
      const stopWaiting = () => {
        hasDisconnected = true;
        observer.disconnect();
        if (poll) clearInterval(poll); // the fallback poll must not schedule a second setup
      };
      const observer = new MutationObserver(() => {
        if (hasDisconnected) return; // Prevent multiple callbacks

        if (this.facetsReady(sidebar)) {
          ssFFDebugLog('[SS Filters] Filters detected!');
          stopWaiting();
          // Use requestAnimationFrame to ensure SearchSpring has finished rendering
          requestAnimationFrame(() => {
            setTimeout(() => this.setupFilters(), 150);
          });
        }
      });

      // Observe the whole sidebar subtree: SearchSpring adds its wrapper as
      // the single direct child first and then renders the facets *inside*
      // it, so a childList-only watch on direct children never fires again
      // for the mutation that actually matters. The callback is a cheap
      // facet count and disconnects on the first successful render.
      observer.observe(sidebar, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });

      // Fallback: poll every 200ms for 20 seconds
      let attempts = 0;
      poll = setInterval(() => {
        attempts++;
        if (hasDisconnected) { clearInterval(poll); return; }
        if (this.facetsReady(sidebar)) {
          stopWaiting();
          setTimeout(() => this.setupFilters(), 150);
        } else if (attempts >= 100) {
          clearInterval(poll);
          observer.disconnect();
          this._waitingForFilters = false;
          ssFFDebugLog('[SS Filters] Filters never appeared');
          // One bounded last-chance retry for a very late SearchSpring paint
          // (nothing else re-enters init after the 3s retry has passed).
          if (!this._gaveUpRetryScheduled) {
            this._gaveUpRetryScheduled = true;
            setTimeout(() => {
              if (!this.initialized) this.init();
            }, 10000);
          }
        }
      }, 200);
    },

    // ------------------------------------------------------------------------
    // Re-arm the waiter after a setup pass that produced no filters.
    // Without this the only remaining attempt was the 3s init retry, so a
    // sidebar that paints its facets later left the row empty for the rest
    // of the page's life unless the shopper forced a sidebar mutation.
    // Bounded, and abandoned as soon as a render succeeds.
    // ------------------------------------------------------------------------
    // A flat 1s cadence both missed an early paint by up to a second and ran
    // out of attempts after ten. Retry quickly at first (the common case is
    // a facet list a few hundred ms away) and back off for the long tail,
    // which covers ~25s instead of ~10s.
    _setupRetries: 0,
    _retryDelays: [250, 400, 600, 900, 1300, 1800, 2400, 3000, 3000, 3000, 3000, 3000],
    scheduleSetupRetry: function() {
      if (this._setupRetries >= this._retryDelays.length) return;
      const delay = this._retryDelays[this._setupRetries];
      this._setupRetries++;
      setTimeout(() => {
        if (this.initialized) return;
        this.waitForFilters();
      }, delay);
    },

    // ------------------------------------------------------------------------
    // Placeholder chips for the window before SearchSpring returns its facets.
    // That wait is third-party (Snap's bundle + search response) and cannot be
    // shortened from the theme, so the row holds its size with inert chips
    // instead of sitting empty and shunting the grid when the real buttons
    // land. Same height and min-width as a real chip: no reflow on swap.
    // ------------------------------------------------------------------------
    renderPlaceholder: function() {
      const topBar = document.querySelector(CONFIG.topBarSelector);
      if (!topBar) return;
      if (topBar.querySelector('.searchspring-featured-filter-item')) return;
      if (topBar.querySelector('.searchspring-featured-filter-placeholder')) return;

      const count = Math.min(this.featuredFilterNames.length, 6);
      if (count < 1) return;

      for (let i = 0; i < count; i++) {
        const chip = document.createElement('span');
        chip.className = 'searchspring-featured-filter-placeholder';
        chip.setAttribute('aria-hidden', 'true');
        topBar.appendChild(chip);
      }
      topBar.classList.add('has-filters', 'is-placeholder');

      // Do not leave a skeleton on screen forever if the facets never arrive.
      setTimeout(() => {
        if (!topBar.querySelector('.searchspring-featured-filter-item')) {
          this.removePlaceholder(topBar);
        }
      }, 30000);
    },

    removePlaceholder: function(topBar) {
      const bar = topBar || document.querySelector(CONFIG.topBarSelector);
      if (!bar) return;
      const chips = bar.querySelectorAll('.searchspring-featured-filter-placeholder');
      if (!chips.length && !bar.classList.contains('is-placeholder')) return;
      chips.forEach(chip => chip.remove());
      bar.classList.remove('is-placeholder');
      if (!bar.querySelector('.searchspring-featured-filter-item')) {
        bar.classList.remove('has-filters');
      }
    },

    // ------------------------------------------------------------------------
    // STEP 4: Find featured filters and clone them to top bar
    // ------------------------------------------------------------------------
    setupFilters: function() {
      this._waitingForFilters = false;
      const sidebar = document.querySelector(CONFIG.sidebarSelector);
      const topBar = document.querySelector(CONFIG.topBarSelector);
      
      if (!sidebar || !topBar) return;

      // Build into a detached container and swap only once the replacement
      // row is complete. Clearing the live row first meant every rebuild —
      // including the ones a burst of facet clicks provokes — left the bar
      // empty for as long as the (third-party) facet render took, which is
      // what blanked the row for ~10s after a rapid apply/remove.
      const stage = document.createElement('div');

      ssFFDebugLog('[SS Filters] Setting up filters...');

      // Find all filter groups in sidebar
      const filterGroups = sidebar.querySelectorAll('.ss__facet');
      ssFFDebugLog('[SS Filters] Found', filterGroups.length, 'filter groups');
      
      // Log all available filter labels for debugging
      const availableLabels = Array.from(filterGroups).map(fg => this.getFilterLabel(fg));
      ssFFDebugLog('[SS Filters] Available filter labels:', availableLabels);
      ssFFDebugLog('[SS Filters] Looking for featured filters:', this.featuredFilterNames);

      // For each featured filter name, find matching filter and clone it
      let filtersCreated = 0;
      this.featuredFilterNames.forEach(featuredName => {
        try {
          const matchedFilter = this.findMatchingFilter(filterGroups, featuredName);
          if (matchedFilter) {
            const matchedLabel = this.getFilterLabel(matchedFilter);
            ssFFDebugLog('[SS Filters] ✓ Found filter:', featuredName, '→ matched to:', matchedLabel);
            const beforeCount = stage.querySelectorAll('.searchspring-featured-filter-item').length;
            this.cloneFilterToTopBar(matchedFilter, stage, featuredName);
            if (stage.querySelectorAll('.searchspring-featured-filter-item').length > beforeCount) {
              filtersCreated++;
            }
          } else {
            ssFFDebugLog('[SS Filters] ✗ Filter not found:', featuredName, '- Available:', availableLabels);
          }
        } catch (error) {
          ssFFDebugLog('[SS Filters] Error creating filter:', featuredName, error);
        }
      });

      ssFFDebugLog('[SS Filters] Created', filtersCreated, 'out of', this.featuredFilterNames.length, 'featured filters');
      
      // Only mark as initialized if we created at least one filter
      if (filtersCreated > 0) {
        // Atomic swap: placeholder and any previous row come out in the same
        // frame the new one goes in, so the bar is never visibly empty.
        this.removePlaceholder(topBar);
        topBar.querySelectorAll('.searchspring-featured-filter-item').forEach(filter => filter.remove());
        while (stage.firstChild) {
          topBar.appendChild(stage.firstChild);
        }
        topBar.classList.add('has-filters');
        this.initialized = true;
      } else {
        // Nothing was built: the facets this collection features are not in
        // the sidebar yet. Leave whatever is on screen standing and try
        // again rather than dead-ending here.
        this.scheduleSetupRetry();
      }

      // Sync top bar with sidebar state after initial setup
      setTimeout(() => {
        this.syncTopBarWithSidebar();
      }, 1000);
      
      // Watch for SearchSpring sidebar updates and re-sync
      this.watchForSidebarUpdates();
    },

    // ------------------------------------------------------------------------
    // Watch for SearchSpring sidebar updates and sync top bar
    // ------------------------------------------------------------------------
    watchForSidebarUpdates: function() {
      const sidebar = document.querySelector(CONFIG.sidebarSelector);
      if (!sidebar) return;
      if (this._sidebarObserver) return; // one persistent observer per page

      // Watch for changes in the sidebar (SearchSpring updates after filter clicks)
      const observer = new MutationObserver(() => {
        // Debounce: wait a bit for SearchSpring to finish updating
        clearTimeout(this.syncTimeout);
        this.syncTimeout = setTimeout(() => {
          // A sidebar re-render while the top bar is still empty is the
          // moment to BUILD the row, not only to sync it: syncTopBarWith-
          // Sidebar only walks filters that already exist, so on its own it
          // could never recover a first pass that cloned nothing.
          const topBar = document.querySelector(CONFIG.topBarSelector);
          if (topBar &&
              !topBar.querySelector('.searchspring-featured-filter-item') &&
              this.facetsReady(sidebar)) {
            this.setupFilters();
            return;
          }
          this.syncTopBarWithSidebar();
        }, 500);
      });

      this._sidebarObserver = observer;
      observer.observe(sidebar, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'] // Watch for class changes (selected states)
      });
    },

    // ------------------------------------------------------------------------
    // Find filter that matches featured filter name
    // ------------------------------------------------------------------------
    findMatchingFilter: function(filterGroups, featuredName) {
      const featuredLower = featuredName.toLowerCase().trim();
      if (!featuredLower) return null; // a blank configured name must not match anything

      for (let filter of filterGroups) {
        const label = this.getFilterLabel(filter);
        const labelLower = label.toLowerCase().trim();
        if (!labelLower) continue; // an unlabelled facet must not match by empty-string inclusion

        // Exact or partial match
        if (labelLower === featuredLower || 
            labelLower.includes(featuredLower) || 
            featuredLower.includes(labelLower)) {
          return filter;
        }
      }
      return null;
    },

    // ------------------------------------------------------------------------
    // Get filter label text from filter element
    // ------------------------------------------------------------------------
    getFilterLabel: function(filterElement) {
      // Try button with class ss__facet__header
      const button = filterElement.querySelector('.ss__facet__header');
      if (button) {
        const text = (button.textContent || button.innerText || '').trim();
        if (text) return text;
      }
      
      // Try other label selectors
      const label = filterElement.querySelector('label, legend, h3, h4');
      if (label) {
        return (label.textContent || label.innerText || '').trim();
      }
      
      return '';
    },

    // ------------------------------------------------------------------------
    // Clone filter to top bar as custom dropdown (not native select)
    // Creates a styled dropdown that matches SearchSpring's checkbox UI
    // For price filters, clones the slider instead
    // ------------------------------------------------------------------------
    cloneFilterToTopBar: function(sourceFilter, container, filterName) {
      ssFFDebugLog('[SS Filters] Cloning filter:', filterName);

      // Check if this is a price/slider filter
      const isPriceFilter = this.isPriceSliderFilter(sourceFilter);
      
      if (isPriceFilter) {
        // Clone the price slider
        this.clonePriceSliderToTopBar(sourceFilter, container, filterName);
        return;
      }

      // Create wrapper div
      const wrapper = document.createElement('div');
      wrapper.className = 'searchspring-featured-filter-item';

      // Get filter label
      const filterLabel = this.getFilterLabel(sourceFilter);

      // Extract options from SearchSpring filter
      const options = this.extractFilterOptions(sourceFilter);
      
      // Always show just the filter label, no count or changes
      const displayText = filterLabel;

      // Create button to trigger dropdown
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'searchspring-featured-filter-button';
      button.setAttribute('data-filter-name', filterName);
      button.setAttribute('aria-label', filterLabel);
      button.setAttribute('aria-expanded', 'false');
      
      // Button text
      const buttonText = document.createElement('span');
      buttonText.className = 'searchspring-featured-filter-button-text';
      buttonText.textContent = displayText;
      button.appendChild(buttonText);
      
      // Dropdown arrow
      const arrow = document.createElement('span');
      arrow.className = 'searchspring-featured-filter-arrow';
      arrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99948 8.78092L11.2993 5.48111L12.2421 6.42392L7.99948 10.6666L3.75684 6.42392L4.69965 5.48111L7.99948 8.78092Z" fill="#4A4E54"/></svg>';
      button.appendChild(arrow);

      // Create dropdown menu (hidden by default)
      const dropdown = document.createElement('div');
      dropdown.className = 'searchspring-featured-filter-dropdown';
      
      // Add options as clickable items (like SearchSpring's <li> elements)
      options.forEach(option => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'searchspring-featured-filter-option';
        item.setAttribute('aria-pressed', option.selected ? 'true' : 'false');
        if (option.selected) {
          item.classList.add('is-selected');
        }
        item.setAttribute('data-value', option.value);
        item.setAttribute('data-href', option.href || '');
        
        // Checkbox indicator
        const checkbox = document.createElement('span');
        checkbox.className = 'searchspring-featured-filter-checkbox';
        if (option.selected) {
          checkbox.classList.add('is-checked');
        }
        item.appendChild(checkbox);
        
        // Option text with count (matches SearchSpring format: "Black (48)")
        const label = document.createElement('span');
        label.className = 'searchspring-featured-filter-option-label';
        if (option.count) {
          label.textContent = option.label + ' (' + option.count + ')';
        } else {
          label.textContent = option.fullLabel || option.label;
        }
        item.appendChild(label);
        
        // Click handler - pass filterName so we can re-find the filter dynamically
        item.addEventListener('click', () => {
          this.handleFilterOptionClick(item, filterName, button, dropdown);
        });
        
        dropdown.appendChild(item);
      });

      // Toggle dropdown on button click
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.contains('is-open');
        this.closeAllDropdowns();
        if (!isOpen) {
          dropdown.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
        }
      });

      // Close dropdown when clicking outside (via closeAllDropdowns so the
      // deferred re-sync catches up any updates skipped while it was open)
      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target) && dropdown.classList.contains('is-open')) {
          this.closeAllDropdowns();
        }
      });

      // Store filter name for re-syncing (we re-find the filter dynamically, not store a reference)
      wrapper.setAttribute('data-source-filter-name', filterName);

      // Add to DOM
      wrapper.appendChild(button);
      wrapper.appendChild(dropdown);
      container.appendChild(wrapper);
      container.classList.add('has-filters');

      const selectedCount = options.filter(opt => opt.selected).length;
      ssFFDebugLog('[SS Filters] ✓ Filter cloned:', filterName, 'Options:', options.length, 'Selected:', selectedCount);
    },
    
    // ------------------------------------------------------------------------
    // Close all open dropdowns
    // ------------------------------------------------------------------------
    closeAllDropdowns: function() {
      let closedAny = false;
      document.querySelectorAll('.searchspring-featured-filter-dropdown.is-open').forEach(dropdown => {
        dropdown.classList.remove('is-open');
        closedAny = true;
        const button = dropdown.previousElementSibling;
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
      });
      // A dropdown skips sidebar re-syncs while open (so a rebuild can't
      // swallow a tap); catch it up now that it's closed.
      if (closedAny) {
        clearTimeout(this.syncTimeout);
        this.syncTimeout = setTimeout(() => {
          this.syncTopBarWithSidebar();
        }, 250);
      }
    },
    
    // ------------------------------------------------------------------------
    // Handle option click in custom dropdown
    // Always re-find the filter in sidebar and get current href (not stored which becomes stale)
    // ------------------------------------------------------------------------
    handleFilterOptionClick: function(optionElement, filterName, button, dropdown) {
      // Get the label text from the clicked option
      const optionLabel = optionElement.querySelector('.searchspring-featured-filter-option-label');
      if (!optionLabel) {
        ssFFDebugLog('[SS Filters] No label found for clicked option');
        return;
      }
      
      // Get label text without count (e.g., "Black" from "Black (18)")
      const optionLabelText = optionLabel.textContent.replace(/\s*\(\d+\)$/, '').trim().toLowerCase();
      
      ssFFDebugLog('[SS Filters] Option clicked:', filterName, optionLabelText);
      
      // Close dropdown before navigation
      dropdown.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      
      // Re-find the filter in sidebar (don't use stored reference - it becomes stale)
      const sidebar = document.querySelector(CONFIG.sidebarSelector);
      if (!sidebar) {
        ssFFDebugLog('[SS Filters] Sidebar not found');
        return;
      }
      
      const filterGroups = sidebar.querySelectorAll('.ss__facet');
      const matchedFilter = this.findMatchingFilter(filterGroups, filterName);
      
      if (!matchedFilter) {
        ssFFDebugLog('[SS Filters] Could not find filter:', filterName);
        return;
      }
      
      // Find matching option in sidebar by label text (always get current href)
      const listItems = matchedFilter.querySelectorAll('li.ss__facet-options-list__option');
      
      for (let li of listItems) {
        const liText = (li.textContent || li.innerText || '').trim();
        const liLabelText = liText.replace(/\s*\(\d+\)$/, '').trim().toLowerCase();
        
        if (liLabelText === optionLabelText) {
          // Found matching option - get current href
          const link = li.querySelector('a') || li;
          let currentHref = link.getAttribute('href') || link.href || '';
          
          if (currentHref) {
            // Make sure it's a full URL if needed
            if (currentHref.startsWith('/')) {
              currentHref = window.location.origin + currentHref;
            } else if (currentHref.startsWith('#')) {
              currentHref = window.location.pathname + currentHref;
            }
            
            ssFFDebugLog('[SS Filters] Navigating to:', currentHref);
            // Click SearchSpring's own link where there is one: Snap
            // intercepts clicks on its facet anchors and updates the results
            // in place, which leaves this row standing. Assigning
            // location.href instead forces a document load every time, so a
            // burst of clicks tore the row down and left it down for the
            // whole of the next facet render. Fall back to the assignment if
            // the click changed nothing.
            const hrefBefore = window.location.href;
            if (link.tagName === 'A' && typeof link.click === 'function') {
              link.click();
              setTimeout(() => {
                // Only fall back when nothing happened at all: an unchanged
                // URL *and* a sidebar option still sitting in the document
                // (SearchSpring re-renders its facets when it handles a
                // click, which detaches this node).
                if (window.location.href === hrefBefore && li.isConnected) {
                  window.location.href = currentHref;
                }
              }, 700);
            } else {
              window.location.href = currentHref;
            }
            return;
          }
          break;
        }
      }
      
      // Fallback: if we couldn't find a match, log error
      ssFFDebugLog('[SS Filters] Could not find matching sidebar option for:', optionLabelText);
    },

    // ------------------------------------------------------------------------
    // Rebuild dropdown options to match current SearchSpring sidebar options
    // This ensures the top bar only shows options that are currently available
    // ------------------------------------------------------------------------
    rebuildDropdownOptions: function(dropdown, currentOptions, filterName, button) {
      // Never tear down a dropdown the shopper has open — replacing the option
      // under their pointer between mousedown and mouseup swallows the tap.
      // Facet clicks make SearchSpring re-render the sidebar, which re-syncs
      // (and rebuilds) once the dropdown is closed again.
      if (dropdown.classList.contains('is-open')) return;
      // Clear existing options
      dropdown.innerHTML = '';
      
      // Rebuild options from current sidebar state
      currentOptions.forEach(option => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'searchspring-featured-filter-option';
        item.setAttribute('aria-pressed', option.selected ? 'true' : 'false');
        if (option.selected) {
          item.classList.add('is-selected');
        }
        item.setAttribute('data-value', option.value);
        item.setAttribute('data-href', option.href || '');
        
        // Checkbox indicator
        const checkbox = document.createElement('span');
        checkbox.className = 'searchspring-featured-filter-checkbox';
        if (option.selected) {
          checkbox.classList.add('is-checked');
        }
        item.appendChild(checkbox);
        
        // Option text with count (matches SearchSpring format: "Black (48)")
        const label = document.createElement('span');
        label.className = 'searchspring-featured-filter-option-label';
        if (option.count) {
          label.textContent = option.label + ' (' + option.count + ')';
        } else {
          label.textContent = option.fullLabel || option.label;
        }
        item.appendChild(label);
        
        // Click handler - pass filterName so we can re-find the filter dynamically
        item.addEventListener('click', () => {
          this.handleFilterOptionClick(item, filterName, button, dropdown);
        });
        
        dropdown.appendChild(item);
      });
    },

    // ------------------------------------------------------------------------
    // Sync top bar dropdown state with SearchSpring sidebar state
    // Called after SearchSpring updates to ensure UI matches sidebar
    // Now also rebuilds options to match what SearchSpring is currently showing
    // ------------------------------------------------------------------------
    syncTopBarWithSidebar: function() {
      const sidebar = document.querySelector(CONFIG.sidebarSelector);
      const topBar = document.querySelector(CONFIG.topBarSelector);
      if (!sidebar || !topBar) return;

      // Find all filter groups in sidebar
      const filterGroups = sidebar.querySelectorAll('.ss__facet');
      
      // For each featured filter in top bar, sync with sidebar
      const topBarFilters = topBar.querySelectorAll('.searchspring-featured-filter-item');
      topBarFilters.forEach(wrapper => {
        const filterName = wrapper.getAttribute('data-source-filter-name');
        if (!filterName) return;

        // Skip price filters - they have their own sync logic
        if (wrapper.classList.contains('searchspring-featured-filter-item--price')) {
          return;
        }

        // Find matching filter in sidebar
        const matchedFilter = this.findMatchingFilter(filterGroups, filterName);
        if (!matchedFilter) return;

        // Extract current options from sidebar (this gets the CURRENT state)
        const currentOptions = this.extractFilterOptions(matchedFilter);
        const selectedOptions = currentOptions.filter(opt => opt.selected);
        
        ssFFDebugLog('[SS Filters] Syncing', filterName, '- Options:', currentOptions.length, 'Selected:', selectedOptions.length, selectedOptions.map(o => o.label));
        
        // Get button and dropdown elements
        const button = wrapper.querySelector('.searchspring-featured-filter-button');
        const dropdown = wrapper.querySelector('.searchspring-featured-filter-dropdown');
        const filterLabel = this.getFilterLabel(matchedFilter);
        
        if (!button || !dropdown) return;

        // Always show just the filter label, no count or changes
        const buttonText = button.querySelector('.searchspring-featured-filter-button-text');
        if (buttonText) {
          buttonText.textContent = filterLabel;
        }

        // Rebuild dropdown options to match current sidebar options
        // This ensures we only show options that SearchSpring is currently displaying
        this.rebuildDropdownOptions(dropdown, currentOptions, filterName, button);
      });
    },

    // ------------------------------------------------------------------------
    // Check if filter is a price/slider type filter
    // ------------------------------------------------------------------------
    isPriceSliderFilter: function(filterElement) {
      // Check for slider/range inputs
      const hasRangeInput = filterElement.querySelector('input[type="range"]') !== null;
      const hasSlider = filterElement.querySelector('.ss__facet-slider, .ss__facet-range, [class*="slider"], [class*="range"]') !== null;
      const hasNoListItems = filterElement.querySelectorAll('li.ss__facet-options-list__option').length === 0;
      
      // Also check if filter label contains "price" (case insensitive)
      const filterLabel = this.getFilterLabel(filterElement).toLowerCase();
      const isPriceLabel = filterLabel.includes('price');
      
      // Price filters typically have no list items but have slider/range inputs
      // OR have "price" in the label
      return hasRangeInput || (hasSlider && hasNoListItems) || (isPriceLabel && hasNoListItems);
    },

    // ------------------------------------------------------------------------
    // Clone price slider filter to top bar
    // ------------------------------------------------------------------------
    clonePriceSliderToTopBar: function(sourceFilter, container, filterName) {
      ssFFDebugLog('[SS Filters] Cloning price slider filter:', filterName);

      // Create wrapper div
      const wrapper = document.createElement('div');
      wrapper.className = 'searchspring-featured-filter-item searchspring-featured-filter-item--price';

      // Get filter label
      const filterLabel = this.getFilterLabel(sourceFilter);

      // Create button to trigger dropdown (contains the slider)
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'searchspring-featured-filter-button';
      button.setAttribute('data-filter-name', filterName);
      button.setAttribute('aria-label', filterLabel);
      button.setAttribute('aria-expanded', 'false');
      
      // Button text
      const buttonText = document.createElement('span');
      buttonText.className = 'searchspring-featured-filter-button-text';
      buttonText.textContent = filterLabel;
      button.appendChild(buttonText);
      
      // Dropdown arrow
      const arrow = document.createElement('span');
      arrow.className = 'searchspring-featured-filter-arrow';
      arrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99948 8.78092L11.2993 5.48111L12.2421 6.42392L7.99948 10.6666L3.75684 6.42392L4.69965 5.48111L7.99948 8.78092Z" fill="#4A4E54"/></svg>';
      button.appendChild(arrow);

      // Create dropdown menu with our own custom slider
      const dropdown = document.createElement('div');
      dropdown.className = 'searchspring-featured-filter-dropdown searchspring-featured-filter-dropdown--price';
      
      // Find SearchSpring's slider to read min/max values
      const ssSlider = sourceFilter.querySelector('.ss__facet-slider');
      if (!ssSlider) {
        ssFFDebugLog('[SS Filters] SearchSpring price slider not found!');
        return;
      }
      
      // Store references
      wrapper._ssSlider = ssSlider;
      wrapper._sourceFilter = sourceFilter;
      
      // Function to get current min/max from SearchSpring slider
      const getPriceRange = () => {
        const handles = ssSlider.querySelectorAll('button[role="slider"]');
        if (handles.length < 2) {
          return { min: 100, max: 1000 }; // Fallback
        }
        
        const minHandle = handles[0];
        const maxHandle = handles[1];
        
        const min = parseInt(minHandle.getAttribute('aria-valuenow') || minHandle.getAttribute('aria-valuemin') || '100');
        const max = parseInt(maxHandle.getAttribute('aria-valuenow') || maxHandle.getAttribute('aria-valuemax') || '1000');
        const minRange = parseInt(minHandle.getAttribute('aria-valuemin') || '100');
        const maxRange = parseInt(maxHandle.getAttribute('aria-valuemax') || '1000');
        
        return { min, max, minRange, maxRange };
      };
      
      // Function to create our custom slider
      const createCustomSlider = () => {
        const range = getPriceRange();
        
        // Create slider container
        const sliderWrapper = document.createElement('div');
        sliderWrapper.className = 'searchspring-custom-price-slider';
        
        // Create min/max display
        const displayWrapper = document.createElement('div');
        displayWrapper.className = 'searchspring-price-display';
        displayWrapper.style.display = 'flex';
        displayWrapper.style.justifyContent = 'space-between';
        displayWrapper.style.marginBottom = '15px';
        displayWrapper.style.fontSize = '14px';
        displayWrapper.style.color = '#333333';
        
        const minDisplay = document.createElement('span');
        minDisplay.className = 'searchspring-price-min';
        minDisplay.textContent = ssFFCurrencySymbol() + range.min;
        
        const maxDisplay = document.createElement('span');
        maxDisplay.className = 'searchspring-price-max';
        maxDisplay.textContent = ssFFCurrencySymbol() + range.max;
        
        displayWrapper.appendChild(minDisplay);
        displayWrapper.appendChild(maxDisplay);
        
        // Create range inputs
        const sliderContainer = document.createElement('div');
        sliderContainer.className = 'searchspring-price-range-inputs';
        sliderContainer.style.position = 'relative';
        sliderContainer.style.height = '40px';
        sliderContainer.style.margin = '0';
        sliderContainer.style.width = '100%';
        sliderContainer.style.display = 'block';
        sliderContainer.style.overflow = 'hidden'; // Clip excess
        sliderContainer.style.boxSizing = 'border-box';
        
        // Debug: Log container setup
        ssFFDebugLog('[SS Price Slider] Container created:', {
          height: '40px',
          width: '100%',
          overflow: 'hidden'
        });
        
        // Helper to round to nearest 50
        const roundToFifty = (val) => {
          return Math.round(val / 50) * 50;
        };
        
        // Calculate vertical positions
        // Track is 6px tall at top: 17px, so center is at 20px
        // Thumb is 18px tall, browser centers it on the input's track
        // Input has height: 6px, so if input is at top: 17px, its track center is at 20px
        // Browser will center 18px thumb on that track, so thumb center = 20px ✓
        // Therefore: input top should equal track top
        const trackTop = 17; // Original position
        const trackHeight = 6;
        const thumbHeight = 18;
        const trackCenter = trackTop + (trackHeight / 2); // 17 + 3 = 20px
        const inputTop = trackTop; // Input aligns with track so thumb centers correctly
        
        ssFFDebugLog('[SS Price Slider] Positioning:', {
          trackTop,
          trackHeight,
          thumbHeight,
          trackCenter,
          inputTop,
          note: 'Input aligned with track so browser centers thumb on track center'
        });
        
        // Min input - positioned at left: 5px
        const minInput = document.createElement('input');
        minInput.type = 'range';
        minInput.className = 'searchspring-price-input searchspring-price-input-min';
        minInput.min = range.minRange;
        minInput.max = range.maxRange;
        minInput.value = roundToFifty(range.min);
        minInput.step = '50';
        minInput.style.position = 'absolute';
        minInput.style.width = 'calc(100% - 18px)';
        minInput.style.height = trackHeight + 'px';
        minInput.style.top = inputTop + 'px';
        minInput.style.left = '5px';
        minInput.style.zIndex = '3';
        minInput.style.appearance = 'none';
        minInput.style.background = 'transparent';
        minInput.style.outline = 'none';
        minInput.style.cursor = 'pointer';
        
        // Max input (overlay) - positioned at left: -7px
        const maxInput = document.createElement('input');
        maxInput.type = 'range';
        maxInput.className = 'searchspring-price-input searchspring-price-input-max';
        maxInput.min = range.minRange;
        maxInput.max = range.maxRange;
        maxInput.value = roundToFifty(range.max);
        maxInput.step = '50';
        maxInput.style.position = 'absolute';
        maxInput.style.width = 'calc(100% - 18px)';
        maxInput.style.height = trackHeight + 'px';
        maxInput.style.top = inputTop + 'px';
        maxInput.style.left = '-7px';
        maxInput.style.zIndex = '4';
        maxInput.style.appearance = 'none';
        maxInput.style.background = 'transparent';
        maxInput.style.outline = 'none';
        maxInput.style.cursor = 'pointer';
        
        // Track/rail - positioned at left: 18px
        const track = document.createElement('div');
        track.className = 'searchspring-price-track';
        track.style.position = 'absolute';
        track.style.width = 'calc(100% - 18px)';
        track.style.height = trackHeight + 'px';
        track.style.top = trackTop + 'px';
        track.style.left = '18px';
        track.style.backgroundColor = 'rgb(244, 244, 244)'; // Match SearchSpring grey
        track.style.borderRadius = '3px';
        track.style.border = 'none';
        track.style.zIndex = '1';
        
        // Active segment - positioned at left: 0px
        const activeSegment = document.createElement('div');
        activeSegment.className = 'searchspring-price-active';
        activeSegment.style.position = 'absolute';
        activeSegment.style.height = trackHeight + 'px';
        activeSegment.style.top = trackTop + 'px';
        activeSegment.style.left = '0px';
        activeSegment.style.backgroundColor = 'rgb(51, 51, 51)'; // #333333
        activeSegment.style.borderRadius = '3px';
        activeSegment.style.border = 'none';
        activeSegment.style.zIndex = '2'; // Lower than thumbs (100+)
        activeSegment.style.pointerEvents = 'none';
        
        // Update active segment position
        // Segment should extend to cover the full thumb area (not just thumb center)
        // Thumbs are 18px wide, so half (9px) extends left/right of center
        // When thumb is at 0%, center is at 0% but thumb extends 9px left
        // Segment should start 9px to the left of thumb center to cover the thumb area
        // When thumb is at 100%, center is at 100% but thumb extends 9px right
        // Segment should end 9px to the right of thumb center (overflow clips excess)
        const updateActiveSegment = () => {
          const minVal = parseInt(minInput.value);
          const maxVal = parseInt(maxInput.value);
          const rangeTotal = range.maxRange - range.minRange;
          
          // Get container width
          const containerWidth = sliderContainer.offsetWidth;
          
          // Track position and width (matching CSS - this is what's visually shown)
          const trackLeft = 18; // px - track starts at 18px
          const trackWidth = containerWidth - 18; // calc(100% - 18px)
          
          // Input positions and widths (matching CSS)
          const minInputLeft = 5; // px
          const maxInputLeft = -7; // px
          const inputWidth = containerWidth - 18; // calc(100% - 18px)
          
          // Calculate value percentages (0-100% of the value range)
          const minValuePercent = ((minVal - range.minRange) / rangeTotal) * 100;
          const maxValuePercent = ((maxVal - range.minRange) / rangeTotal) * 100;
          
          // CRITICAL: Browser positions thumbs relative to the INPUT element's coordinate system
          // The browser calculates thumb center within the input's track (pseudo-element)
          // The input's track spans the input's width, starting at the input's left edge
          // Then CSS transforms are applied relative to that position
          
          // Calculate where browser positions thumb centers (before CSS transforms)
          // Browser positions thumb at: inputLeft + (percentage * inputWidth)
          // Min input: browser positions thumb at minInputLeft + (percentage * inputWidth)
          const minThumbCenterBeforeTransform = minInputLeft + (minValuePercent / 100 * inputWidth);
          // Max input: browser positions thumb at maxInputLeft + (percentage * inputWidth)
          const maxThumbCenterBeforeTransform = maxInputLeft + (maxValuePercent / 100 * inputWidth);
          
          // Account for CSS transforms that shift the thumbs visually
          // Left thumb has translateX(-5px), so it appears 5px to the left of its calculated position
          const leftThumbTransformShift = -5; // px
          // Right thumb has translateX(7px), so it appears 7px to the right of its calculated position
          const rightThumbTransformShift = 7; // px
          
          // Apply CSS transforms to get final visual position relative to container (left: 0px)
          // For left thumb: The transform was designed to align with track, so we need to account
          // for the offset between input position (5px) and track position (18px)
          // The original working calculation was: trackLeft + (percentage * trackWidth) + transform
          // Which equals: 18px + (percentage * trackWidth) - 5px = 13px + (percentage * trackWidth)
          // So we need: inputLeft + (percentage * inputWidth) + transform + (trackLeft - inputLeft)
          const minThumbCenterPx = minThumbCenterBeforeTransform + leftThumbTransformShift + (trackLeft - minInputLeft);
          // For right thumb: The calculation works correctly as-is
          const maxThumbCenterPx = maxThumbCenterBeforeTransform + rightThumbTransformShift;
          
          // Thumb half-width
          const thumbHalfWidth = 9; // 18px / 2
          
          // Segment should go from left edge of min thumb to right edge of max thumb
          // Left edge = thumb center - half width
          // Right edge = thumb center + half width
          const segmentLeftPx = minThumbCenterPx - thumbHalfWidth;
          const segmentRightPx = maxThumbCenterPx + thumbHalfWidth;
          const segmentWidthPx = segmentRightPx - segmentLeftPx;
          
          // Segment starts at left: 0px relative to container
          // So we need to calculate the actual left position and width
          activeSegment.style.left = segmentLeftPx + 'px';
          activeSegment.style.width = segmentWidthPx + 'px';
          
          // Debug logging
          ssFFDebugLog('[SS Price Slider] Update:', {
            minVal,
            maxVal,
            containerWidth,
            trackLeft: trackLeft + 'px',
            trackWidth: trackWidth + 'px',
            inputWidth: inputWidth + 'px',
            minInputLeft: minInputLeft + 'px',
            maxInputLeft: maxInputLeft + 'px',
            minValuePercent: minValuePercent.toFixed(2) + '%',
            maxValuePercent: maxValuePercent.toFixed(2) + '%',
            minThumbCenterBeforeTransform: minThumbCenterBeforeTransform.toFixed(2) + 'px',
            maxThumbCenterBeforeTransform: maxThumbCenterBeforeTransform.toFixed(2) + 'px',
            leftThumbTransformShift: leftThumbTransformShift + 'px',
            rightThumbTransformShift: rightThumbTransformShift + 'px',
            trackInputOffset: (trackLeft - minInputLeft) + 'px',
            minThumbCenterPx: minThumbCenterPx.toFixed(2) + 'px',
            maxThumbCenterPx: maxThumbCenterPx.toFixed(2) + 'px',
            segmentLeftPx: segmentLeftPx.toFixed(2) + 'px',
            segmentRightPx: segmentRightPx.toFixed(2) + 'px',
            segmentWidthPx: segmentWidthPx.toFixed(2) + 'px',
            segmentLeft: activeSegment.style.left,
            segmentWidth: activeSegment.style.width
          });
          
          minDisplay.textContent = ssFFCurrencySymbol() + minVal;
          maxDisplay.textContent = ssFFCurrencySymbol() + maxVal;
        };
        
        // Update on input - round to nearest 50
        minInput.addEventListener('input', (e) => {
          let val = parseInt(minInput.value);
          val = roundToFifty(val);
          if (val > parseInt(maxInput.value)) {
            val = parseInt(maxInput.value);
          }
          minInput.value = val;
          updateActiveSegment();
        });
        
        maxInput.addEventListener('input', (e) => {
          let val = parseInt(maxInput.value);
          val = roundToFifty(val);
          if (val < parseInt(minInput.value)) {
            val = parseInt(minInput.value);
          }
          maxInput.value = val;
          updateActiveSegment();
        });
        
        // Sync to SearchSpring on change
        const syncToSearchSpring = () => {
          const minVal = roundToFifty(parseInt(minInput.value));
          const maxVal = roundToFifty(parseInt(maxInput.value));
          
          // Find SearchSpring handles
          const handles = ssSlider.querySelectorAll('button[role="slider"]');
          if (handles.length >= 2) {
            const minHandle = handles[0];
            const maxHandle = handles[1];
            
            // Calculate percentage positions
            const rangeTotal = range.maxRange - range.minRange;
            const minPercent = ((minVal - range.minRange) / rangeTotal) * 100;
            const maxPercent = ((maxVal - range.minRange) / rangeTotal) * 100;
            
            // Trigger click on SearchSpring handles at calculated positions
            // We'll need to simulate the drag or update the URL directly
            // For now, let's try clicking the handles after moving them
            // Actually, better to update the URL hash that SearchSpring uses
            this.updatePriceFilter(minVal, maxVal, sourceFilter);
          }
        };
        
        minInput.addEventListener('change', syncToSearchSpring);
        maxInput.addEventListener('change', syncToSearchSpring);
        
        // Initial update
        updateActiveSegment();
        
        // Assemble
        sliderContainer.appendChild(track);
        sliderContainer.appendChild(activeSegment);
        sliderContainer.appendChild(minInput);
        sliderContainer.appendChild(maxInput);
        
        sliderWrapper.appendChild(displayWrapper);
        sliderWrapper.appendChild(sliderContainer);
        
        return sliderWrapper;
      };
      
      // Create and add slider to dropdown
      const customSlider = createCustomSlider();
      dropdown.appendChild(customSlider);
      
      // Store slider reference for updates
      wrapper._customSlider = customSlider;
      wrapper._dropdown = dropdown;
      
      // Toggle dropdown on button click
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.contains('is-open');
        this.closeAllDropdowns();
        
        if (!isOpen) {
          // Update slider with current values before showing
          const range = getPriceRange();
          const minInput = customSlider.querySelector('.searchspring-price-input-min');
          const maxInput = customSlider.querySelector('.searchspring-price-input-max');
          const activeSegment = customSlider.querySelector('.searchspring-price-active');
          if (minInput && maxInput) {
            // Helper to round to nearest 50
            const roundToFifty = (val) => Math.round(val / 50) * 50;
            
            minInput.min = range.minRange;
            minInput.max = range.maxRange;
            maxInput.min = range.minRange;
            maxInput.max = range.maxRange;
            minInput.value = roundToFifty(range.min);
            maxInput.value = roundToFifty(range.max);
          }
          
          dropdown.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
          
          // Update active segment after dropdown is visible (layout calculated)
          // Use double RAF to ensure layout is complete
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (minInput && maxInput) {
                // Re-trigger updateActiveSegment by dispatching input event
                minInput.dispatchEvent(new Event('input'));
              }
            });
          });
        } else {
          dropdown.classList.remove('is-open');
          button.setAttribute('aria-expanded', 'false');
        }
      });

      // Close dropdown when clicking outside (via closeAllDropdowns so the
      // deferred re-sync catches up any updates skipped while it was open)
      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target) && dropdown.classList.contains('is-open')) {
          this.closeAllDropdowns();
        }
      });
      
      // Watch for SearchSpring updates and refresh our slider
      const observer = new MutationObserver(() => {
        if (dropdown.classList.contains('is-open')) {
          const range = getPriceRange();
          const minInput = customSlider.querySelector('.searchspring-price-input-min');
          const maxInput = customSlider.querySelector('.searchspring-price-input-max');
          if (minInput && maxInput) {
            // Helper to round to nearest 50
            const roundToFifty = (val) => Math.round(val / 50) * 50;
            
            minInput.min = range.minRange;
            minInput.max = range.maxRange;
            maxInput.min = range.minRange;
            maxInput.max = range.maxRange;
            minInput.value = roundToFifty(range.min);
            maxInput.value = roundToFifty(range.max);
            minInput.dispatchEvent(new Event('input'));
          }
        }
      });
      
      observer.observe(ssSlider, {
        attributes: true,
        attributeFilter: ['aria-valuenow', 'aria-valuemin', 'aria-valuemax'],
        subtree: true
      });

      // Store filter name and slider container reference for re-syncing
      wrapper.setAttribute('data-source-filter-name', filterName);
      wrapper.setAttribute('data-slider-container', 'true');

      // Add to DOM
      wrapper.appendChild(button);
      wrapper.appendChild(dropdown);
      container.appendChild(wrapper);
      container.classList.add('has-filters');

      ssFFDebugLog('[SS Filters] ✓ Price slider created:', filterName);
    },

    // ------------------------------------------------------------------------
    // Update SearchSpring price filter by simulating handle drags and updating URL
    // ------------------------------------------------------------------------
    updatePriceFilter: function(minVal, maxVal, sourceFilter) {
      const ssSlider = sourceFilter.querySelector('.ss__facet-slider');
      if (!ssSlider) {
        ssFFDebugLog('[SS Filters] SearchSpring slider not found');
        return;
      }
      
      const handles = ssSlider.querySelectorAll('button[role="slider"]');
      if (handles.length < 2) {
        ssFFDebugLog('[SS Filters] Not enough handles found');
        return;
      }
      
      const minHandle = handles[0];
      const maxHandle = handles[1];
      
      // Get current range
      const minRange = parseInt(minHandle.getAttribute('aria-valuemin') || '100');
      const maxRange = parseInt(maxHandle.getAttribute('aria-valuemax') || '1000');
      const rangeTotal = maxRange - minRange;
      
      // Calculate percentage positions
      const minPercent = Math.max(0, Math.min(100, ((minVal - minRange) / rangeTotal) * 100));
      const maxPercent = Math.max(0, Math.min(100, ((maxVal - minRange) / rangeTotal) * 100));
      
      // Get slider dimensions for mouse event simulation
      const sliderRect = ssSlider.getBoundingClientRect();
      const sliderWidth = sliderRect.width;
      
      // Simulate mouse events on min handle
      const simulateHandleDrag = (handle, targetPercent, targetValue) => {
        const handleRect = handle.getBoundingClientRect();
        const currentLeft = parseFloat(handle.style.left) || 0;
        const targetLeft = (targetPercent / 100) * sliderWidth;
        
        // Create mouse events to simulate drag
        const mouseDown = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          clientX: handleRect.left + handleRect.width / 2,
          clientY: handleRect.top + handleRect.height / 2,
          button: 0
        });
        
        const mouseMove = new MouseEvent('mousemove', {
          bubbles: true,
          cancelable: true,
          clientX: sliderRect.left + targetLeft,
          clientY: sliderRect.top + sliderRect.height / 2,
          button: 0
        });
        
        const mouseUp = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          clientX: sliderRect.left + targetLeft,
          clientY: sliderRect.top + sliderRect.height / 2,
          button: 0
        });
        
        // Dispatch events in sequence
        handle.dispatchEvent(mouseDown);
        setTimeout(() => {
          handle.dispatchEvent(mouseMove);
          setTimeout(() => {
            handle.dispatchEvent(mouseUp);
          }, 10);
        }, 10);
      };
      
        // Update URL hash - SearchSpring uses format: #/filter:ss_price:min:max
      const updateURLHash = () => {
        const currentHash = window.location.hash;
        let newHash = currentHash;
        
        // SearchSpring hash format appears to be: #/filter:ss_price:min:max
        // Or might be: #ss_price:min:max or #/filter:ss_price-min-max
        const pricePatterns = [
          /(\/filter:ss_price[^:]*:)([^:]+)(:)([^:&]+)/,
          /(ss_price[^:]*:)([^:]+)(:)([^:&]+)/,
          /(ss_price[^:]*-)([^-]+)(-)([^-&]+)/
        ];
        
        let hashUpdated = false;
        for (const pattern of pricePatterns) {
          if (pattern.test(currentHash)) {
            newHash = currentHash.replace(pattern, (match, p1, p2, p3, p4) => {
              return p1 + minVal + p3 + maxVal;
            });
            hashUpdated = true;
            break;
          }
        }
        
        if (!hashUpdated) {
          // Add price filter to hash
          if (currentHash) {
            // Check if hash already has filters
            if (currentHash.includes('/filter:')) {
              newHash = currentHash + '&ss_price:' + minVal + ':' + maxVal;
            } else if (currentHash.includes('#')) {
              newHash = currentHash + '/filter:ss_price:' + minVal + ':' + maxVal;
            } else {
              newHash = currentHash + '#/filter:ss_price:' + minVal + ':' + maxVal;
            }
          } else {
            newHash = '#/filter:ss_price:' + minVal + ':' + maxVal;
          }
        }
        
        if (newHash && newHash !== currentHash) {
          ssFFDebugLog('[SS Filters] Updating URL hash from:', currentHash, 'to:', newHash);
          
          // Update hash
          const oldHash = window.location.hash;
          window.location.hash = newHash;
          
          // Manually trigger hashchange event in case SearchSpring isn't listening
          // (Some frameworks need this)
          if (window.location.hash === oldHash) {
            // Hash didn't change, force it
            window.history.replaceState(null, '', newHash);
            window.dispatchEvent(new HashChangeEvent('hashchange', {
              oldURL: window.location.href.replace(oldHash, ''),
              newURL: window.location.href
            }));
          }
          
          return newHash;
        }
        
        return newHash || currentHash;
      };
      
      // ONLY update URL hash - don't modify SearchSpring's DOM
      // Modifying SearchSpring's slider DOM causes visual glitches
      // SearchSpring will update its own slider when it processes the hash change
      const newHash = updateURLHash();
      
      // Don't simulate drags or modify SearchSpring's DOM - let it handle the update itself
      // This prevents the native slider from getting messed up
    },

    // ------------------------------------------------------------------------
    // Setup proxy for price slider - syncs interactions with SearchSpring's slider
    // Proxies ALL mouse/touch events to the original slider so it remains interactive
    // ------------------------------------------------------------------------
    setupPriceSliderProxy: function(clonedSlider, sourceFilter) {
      // Try to find the original slider/content in sourceFilter
      // Since we might have cloned the entire filter, try multiple approaches
      let originalSlider = sourceFilter.querySelector('.ss__facet-slider, .ss__facet-range, [class*="slider"], [class*="range"]') ||
                          sourceFilter.querySelector('input[type="range"]')?.closest('div');
      
      // If not found, try the facet content/body
      if (!originalSlider) {
        originalSlider = sourceFilter.querySelector('.ss__facet-content, .ss__facet__content, [class*="facet-content"], [class*="facet__content"]') ||
                        sourceFilter.querySelector('.ss__facet-body, .ss__facet__body, [class*="facet-body"], [class*="facet__body"]');
      }
      
      // Last resort: use the entire sourceFilter (minus header)
      if (!originalSlider) {
        originalSlider = sourceFilter;
      }

      // Find slider handles/dots - these are what users click and drag
      const clonedHandles = clonedSlider.querySelectorAll('[class*="handle"], [class*="dot"], [class*="thumb"], [role="slider"], .noUi-handle, [class*="noUi-handle"]');
      const originalHandles = originalSlider.querySelectorAll('[class*="handle"], [class*="dot"], [class*="thumb"], [role="slider"], .noUi-handle, [class*="noUi-handle"]');
      
      // Proxy mouse/touch events from cloned handles to original handles
      clonedHandles.forEach((clonedHandle, index) => {
        const originalHandle = originalHandles[index];
        if (!originalHandle) return;
        
        // Make cloned handle pointer-events work
        clonedHandle.style.pointerEvents = 'auto';
        clonedHandle.style.cursor = 'grab';
        
        // Track if we're dragging
        let isDragging = false;
        
        // Proxy all mouse events
        const mouseEvents = ['mousedown', 'mouseup', 'mousemove', 'click', 'mouseenter', 'mouseleave'];
        mouseEvents.forEach(eventType => {
          clonedHandle.addEventListener(eventType, function(e) {
            // Calculate relative position to proxy the event correctly
            const rect = clonedHandle.getBoundingClientRect();
            const originalRect = originalHandle.getBoundingClientRect();
            
            // For dragging, calculate offset from handle center
            let clientX, clientY;
            if (eventType === 'mousedown') {
              isDragging = true;
              // Store the offset for dragging
              clonedHandle.dataset.dragOffsetX = e.clientX - rect.left;
              clonedHandle.dataset.dragOffsetY = e.clientY - rect.top;
              clientX = originalRect.left + parseFloat(clonedHandle.dataset.dragOffsetX);
              clientY = originalRect.top + parseFloat(clonedHandle.dataset.dragOffsetY);
            } else if (eventType === 'mousemove' && isDragging) {
              // During drag, maintain the offset
              const offsetX = parseFloat(clonedHandle.dataset.dragOffsetX || '0');
              const offsetY = parseFloat(clonedHandle.dataset.dragOffsetY || '0');
              clientX = originalRect.left + offsetX;
              clientY = originalRect.top + offsetY;
            } else if (eventType === 'mouseup') {
              isDragging = false;
              clientX = originalRect.left + (e.clientX - rect.left);
              clientY = originalRect.top + (e.clientY - rect.top);
            } else {
              clientX = originalRect.left + (e.clientX - rect.left);
              clientY = originalRect.top + (e.clientY - rect.top);
            }
            
            // Create new event with adjusted coordinates
            const newEvent = new MouseEvent(eventType, {
              bubbles: true,
              cancelable: true,
              clientX: clientX,
              clientY: clientY,
              button: e.button,
              buttons: e.buttons,
              ctrlKey: e.ctrlKey,
              shiftKey: e.shiftKey,
              altKey: e.altKey,
              metaKey: e.metaKey
            });
            
            originalHandle.dispatchEvent(newEvent);
            
            // For mousedown, prevent default to allow dragging
            if (eventType === 'mousedown') {
              e.preventDefault();
              e.stopPropagation();
            }
          }, { passive: false });
        });
        
        // Also handle global mouseup to stop dragging if mouse leaves the handle
        document.addEventListener('mouseup', function() {
          isDragging = false;
        });
        
        // Proxy all touch events for mobile
        const touchEvents = ['touchstart', 'touchend', 'touchmove', 'touchcancel'];
        touchEvents.forEach(eventType => {
          clonedHandle.addEventListener(eventType, function(e) {
            const rect = clonedHandle.getBoundingClientRect();
            const originalRect = originalHandle.getBoundingClientRect();
            
            // Create new touch event
            const touches = Array.from(e.touches || []).map(touch => {
              return new Touch({
                identifier: touch.identifier,
                target: originalHandle,
                clientX: originalRect.left + (touch.clientX - rect.left),
                clientY: originalRect.top + (touch.clientY - rect.top),
                screenX: touch.screenX,
                screenY: touch.screenY,
                pageX: touch.pageX,
                pageY: touch.pageY,
                radiusX: touch.radiusX,
                radiusY: touch.radiusY,
                rotationAngle: touch.rotationAngle,
                force: touch.force
              });
            });
            
            const newEvent = new TouchEvent(eventType, {
              bubbles: true,
              cancelable: true,
              touches: touches,
              targetTouches: touches,
              changedTouches: touches,
              ctrlKey: e.ctrlKey,
              shiftKey: e.shiftKey,
              altKey: e.altKey,
              metaKey: e.metaKey
            });
            
            originalHandle.dispatchEvent(newEvent);
            
            if (eventType === 'touchstart') {
              e.preventDefault();
            }
          }, { passive: false });
        });
      });
      
      // Also proxy events on the slider track/bar itself (for clicking to set value)
      const clonedTrack = clonedSlider.querySelector('[class*="track"], [class*="bar"], [class*="rail"], .noUi-base, [class*="noUi-base"]');
      const originalTrack = originalSlider.querySelector('[class*="track"], [class*="bar"], [class*="rail"], .noUi-base, [class*="noUi-base"]');
      
      if (clonedTrack && originalTrack) {
        clonedTrack.style.pointerEvents = 'auto';
        clonedTrack.style.cursor = 'pointer';
        
        ['mousedown', 'click', 'touchstart'].forEach(eventType => {
          clonedTrack.addEventListener(eventType, function(e) {
            const rect = clonedTrack.getBoundingClientRect();
            const originalRect = originalTrack.getBoundingClientRect();
            
            if (eventType === 'mousedown' || eventType === 'click') {
              const newEvent = new MouseEvent(eventType, {
                bubbles: true,
                cancelable: true,
                clientX: originalRect.left + (e.clientX - rect.left),
                clientY: originalRect.top + (e.clientY - rect.top),
                button: e.button,
                buttons: e.buttons
              });
              originalTrack.dispatchEvent(newEvent);
            } else if (eventType === 'touchstart') {
              const touch = e.touches[0];
              const newTouch = new Touch({
                identifier: touch.identifier,
                target: originalTrack,
                clientX: originalRect.left + (touch.clientX - rect.left),
                clientY: originalRect.top + (touch.clientY - rect.top),
                screenX: touch.screenX,
                screenY: touch.screenY,
                pageX: touch.pageX,
                pageY: touch.pageY
              });
              const newEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                touches: [newTouch],
                targetTouches: [newTouch],
                changedTouches: [newTouch]
              });
              originalTrack.dispatchEvent(newEvent);
            }
            
            e.preventDefault();
          }, { passive: false });
        });
      }

      // For range/number inputs, sync values (backup for direct input)
      const clonedInputs = clonedSlider.querySelectorAll('input[type="range"], input[type="number"], input[type="text"]');
      const originalInputs = originalSlider.querySelectorAll('input[type="range"], input[type="number"], input[type="text"]');
      
      clonedInputs.forEach((clonedInput, index) => {
        const originalInput = originalInputs[index];
        if (!originalInput) return;

        // Sync value changes from cloned to original
        clonedInput.addEventListener('input', function() {
          originalInput.value = clonedInput.value;
          originalInput.dispatchEvent(new Event('input', { bubbles: true }));
          originalInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        clonedInput.addEventListener('change', function() {
          originalInput.value = clonedInput.value;
          originalInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      // For any buttons, links, or interactive elements, proxy clicks
      const clonedButtons = clonedSlider.querySelectorAll('button, a, [role="button"]');
      const originalButtons = originalSlider.querySelectorAll('button, a, [role="button"]');
      
      clonedButtons.forEach((clonedBtn, index) => {
        const originalBtn = originalButtons[index];
        if (originalBtn) {
          clonedBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            originalBtn.click();
          });
        }
      });
      
      ssFFDebugLog('[SS Filters] Price slider proxy setup complete. Handles:', clonedHandles.length, 'Track:', !!clonedTrack);
    },

    // ------------------------------------------------------------------------
    // Extract filter options from SearchSpring filter element
    // SearchSpring uses <li> elements with href attributes
    // ------------------------------------------------------------------------
    extractFilterOptions: function(filterElement) {
      const options = [];
      
      // Find all option <li> elements
      const listItems = filterElement.querySelectorAll('li.ss__facet-options-list__option');
      
      listItems.forEach(li => {
        const href = li.getAttribute('href') || '';
        const text = (li.textContent || li.innerText || '').trim();
        
        // Extract filter value from href (format: /collections/name#/filter:field:value)
        let value = '';
        if (href) {
          const match = href.match(/filter:([^:]+):(.+?)(?:$|#)/);
          if (match && match[2]) {
            value = decodeURIComponent(match[2].replace(/%25/g, '%'));
          }
        }
        
        // Extract product count from text (format: "Black (48)" or "Blue (40)")
        let count = '';
        let labelWithoutCount = text;
        const countMatch = text.match(/\((\d+)\)$/);
        if (countMatch) {
          count = countMatch[1];
          labelWithoutCount = text.replace(/\s*\(\d+\)$/, '').trim();
        }
        
        // Use text as value if no href value
        const finalValue = value || labelWithoutCount;
        const finalLabel = labelWithoutCount || value;
        
        // Check if selected - SearchSpring uses various classes for selected state
        const isSelected = li.classList.contains('tag--active') || 
                         li.classList.contains('active') ||
                         li.classList.contains('ss__facet-options-list__option--active') ||
                         li.querySelector('a.tag--active') !== null ||
                         li.querySelector('a.active') !== null ||
                         li.getAttribute('aria-selected') === 'true';
        
        if (finalValue && finalLabel) {
          options.push({
            value: finalValue,
            label: finalLabel,
            count: count, // Product count
            fullLabel: text, // Full label with count for display
            href: href,
            selected: isSelected
          });
        }
      });
      
      return options;
    },

  };

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        Manager.init();
      });
    } else {
      Manager.init();
    }
  }

  // Start
  init();

  // Retry after 3 seconds if SearchSpring loads late (only if not already initialized)
  setTimeout(() => {
    if (!Manager.initialized) {
      Manager.init();
    }
  }, 3000);

})();
